import { useCallback, useEffect, useRef, useState } from "react";
import { fetchEngagement } from "@/api/engagement/api";
import type { EngagementEvent, EngagementFilters } from "@/types/engagement";

export function useEngagementFeed() {
  const [items, setItems] = useState<EngagementEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const reset = useCallback(() => {
    requestId.current++;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setLoading(false);
    setLoadingMore(false);
    setError(null);
  }, []);

  const load = useCallback(async (filters: EngagementFilters, append = false) => {
    const currentRequestId = ++requestId.current;
    const setCurrentLoading = append ? setLoadingMore : setLoading;

    if (!append) setLoadingMore(false);
    setCurrentLoading(true);
    setError(null);

    try {
      const data = await fetchEngagement(filters);
      if (currentRequestId !== requestId.current) return;

      setItems(prev => append ? [...prev, ...data.items] : data.items);
      setHasMore(data.hasMore);
      setNextCursor(data.nextCursor ?? null);
    } catch {
      if (currentRequestId === requestId.current) {
        setError("Failed to load Engagement.");
      }
    } finally {
      if (currentRequestId === requestId.current) {
        setCurrentLoading(false);
      }
    }
  }, []);

  useEffect(() => () => {
    requestId.current++;
  }, []);

  return { items, nextCursor, hasMore, loading, loadingMore, error, load, reset };
}